/**
 * Why a task is on the list: the basis flag every step carries (owner, 2026-09-29: "Our tasks need those flags based on
 * the legal and OSHA and FDA criteria, and common sense if not those").
 *
 * | Basis | Means | Comes only from |
 * |---|---|---|
 * | law   | Required by law: the FDA Food Code as Utah adopts it (R392-100), Utah R392 rules, Utah Code, federal food law | A citation string naming one of those (requirements.ts, template rules, surface intervals) |
 * | osha  | OSHA workplace rule (29 CFR 1910, enforced in Utah by UOSH under R614-1-4) | A citation naming a 29 CFR 1904/1910 section: in the repo, or a row of docs/source/research/osha-housekeeping.md / task-guides.md |
 * | maker | The equipment maker's manual or the product label | A citation or source line that says label / maker / manufacturer / manual / an EPA registration |
 * | sense | No rule found: a common-sense best practice, shown as "Suggested" with its one-line reason | Everything else (house protocols, research rows without a rule, no citation) |
 *
 * Gates (test/basis.test.ts):
 * | B1 One basis | every requirement, template, template rule, built-in check, starter task and surface interval gets exactly one |
 * | B2 No invented law | "law" / "osha" only when a real citation string of that kind is present; the citation shown is that string |
 * | B3 Control | an item with no citation is "sense", never "law" |
 * | B4 Law first | an item citing both the Food Code and OSHA shows the Food Code (the stricter, food-specific rule) |
 * OSHA covers an employer with one or more employees (osha-housekeeping OS1); a home never shows OSHA as a rule.
 */
import { REQUIREMENTS } from "./requirements.ts";
import { getTemplate, type RowRule, type SheetTemplate } from "./templates.ts";
import { getSurface, type SurfaceGuide } from "./surfaces.ts";
import { intervalFor, parseIntervalHours, SUGGESTED_REASON } from "./objects.ts";
import { seedForTask, TASK_SEEDS } from "./tasks.ts";
import type { CheckRule } from "./schedule.ts";
import { BASIS_UT } from "./states/ut/basis.ts";
import { perStateTable } from "./states/access.ts";
import { LAW_CITE_PATTERNS } from "./states/bundles/eager-facts.ts";
import type { StateWords, UsState } from "./states/types.ts";

export type Basis = "law" | "osha" | "maker" | "sense";
export const BASES: readonly Basis[] = ["law", "osha", "maker", "sense"];

export interface BasisInfo {
  basis: Basis;
  /** "Required by law", "OSHA", "Maker's instructions", "Suggested". */
  label: string;
  /** The citation behind law / osha (short form: "Food Code 3-501.16", "1910.157(e)(2)"); maker: the source line when there is one. */
  citation?: string;
  /** Sense (and maker without a citation): the one-line reason. */
  reason?: string;
  /** Suggested timing on an object that a rule still asks to keep clean: "Keeping it clean: Food Code 4-601.11(C)". */
  rule?: string;
  /** The row label, ready to show as Info: "Required by law · Food Code 3-501.16", "OSHA · 1910.22", "Maker's instructions", "Suggested". */
  line: string;
}

/** Only law and OSHA items are never dropped under peak or a time budget (maker and sense can wait for the rush to pass). */
export const neverDropBasis = (b: Basis): boolean => b === "law" || b === "osha";

/** Ordering rank among due items: law and OSHA first, then the maker, then common sense. */
export const BASIS_RANK: Record<Basis, number> = { law: 0, osha: 0, maker: 1, sense: 2 };

const WORDS: Record<UsState, StateWords<typeof BASIS_UT>> = perStateTable("BASIS", BASIS_UT);
/** A line citing ANY supported state's law is law, whichever state the place is in: the union of every state's citation patterns. */
const STATE_LAW = Object.values(LAW_CITE_PATTERNS).filter((x): x is string => !!x);
const LAW_RE = new RegExp(String.raw`\b(Food Code (?:\d{4} )?§?\d|${[...STATE_LAW, String.raw`FALCPA|FASTER Act|21 CFR \d|9 CFR \d`].join("|")})`);
const OSHA_RE = /\b(?:29 CFR |OSHA (?:29 CFR )?)(19(?:04|10)\.\d+[\w().–-]*)/;
const MAKER_RE = /\b(label|labels|maker|maker's|manufacturer|manufacturer's|manual)\b|\bEPA \d/i;

export const isLawCitation = (c: string): boolean => LAW_RE.test(c);
export const isOshaCitation = (c: string): boolean => OSHA_RE.test(c);

/** "Food Code 3-501.16; R392-102-12(6)" → its pieces, trimmed. */
const pieces = (cites: readonly string[]): string[] => cites.flatMap((c) => c.split(/;\s*/)).map((c) => c.trim()).filter(Boolean);

/** "Food Code 4-203.11, 4-302.12" → "Food Code 4-203.11": one section on the row; the rest stay in the source. */
const firstSection = (c: string) => c.split(/,\s*/)[0]!.trim();
function lawInfo(full: string): BasisInfo {
  const citation = firstSection(full);
  return { basis: "law", label: "Required by law", citation, line: `Required by law · ${citation}` };
}
function oshaInfo(citation: string): BasisInfo {
  const short = OSHA_RE.exec(citation)?.[1] ?? citation;
  return { basis: "osha", label: "OSHA", citation: short, line: `OSHA · ${short}` };
}
const makerInfo = (citation?: string): BasisInfo => ({ basis: "maker", label: "Maker's instructions", ...(citation ? { citation } : {}), line: "Maker's instructions" });
export const SENSE_REASON = "No rule found; a common-sense best practice.";
const senseInfo = (reason = SENSE_REASON, rule?: string): BasisInfo => ({ basis: "sense", label: "Suggested", reason, ...(rule ? { rule } : {}), line: "Suggested" });

/**
 * The basis of a list of citations (plus optional source text, e.g. a howOften line "as the maker says"): the first law
 * citation, else the first OSHA one, else maker, else sense. Never law or OSHA without such a citation (B2).
 */
export function basisOfCitations(cites: readonly string[], o: { text?: string; reason?: string } = {}): BasisInfo {
  const ps = pieces(cites);
  const law = ps.find(isLawCitation);
  if (law) return lawInfo(law);
  const osha = ps.find(isOshaCitation);
  if (osha) return oshaInfo(osha);
  const maker = ps.find((p) => MAKER_RE.test(p));
  if (maker) return makerInfo(maker);
  if (o.text && MAKER_RE.test(o.text)) return makerInfo();
  return senseInfo(o.reason);
}

/* ─────────────── OSHA rows (docs/source/research) ─────────────── */

/**
 * OSHA rules the research read, for starter tasks that cite no requirement (task key → citation + research row). Only
 * rows whose rule sets what the task does; the test checks each row exists and quotes this section.
 */
export const TASK_OSHA: Record<string, { citation: string; row: string }> = {
  fire: { citation: "OSHA 29 CFR 1910.157(e)(2)", row: "task-guides#TG45" },
};

/**
 * OSHA rules that ask a surface to be kept clean or in order without setting an interval (so the interval stays
 * "Suggested", with this as the rule behind keeping it right). Research: osha-housekeeping.md.
 */
export const SURFACE_OSHA_RULE: Record<string, { citation: string; row: string }> = {
  "kitchen-floor": { citation: "OSHA 29 CFR 1910.22(a)(2)", row: "osha-housekeeping#OS7" },
  "dining-floor": { citation: "OSHA 29 CFR 1910.22(a)(2)", row: "osha-housekeeping#OS7" },
  "restroom-floor": { citation: "OSHA 29 CFR 1910.22(a)(2)", row: "osha-housekeeping#OS7" },
  "hardwood-floor": { citation: "OSHA 29 CFR 1910.22(a)(2)", row: "osha-housekeeping#OS7" },
  "restroom-sink": { citation: "OSHA 29 CFR 1910.141(d)(1)", row: "osha-housekeeping#OS17" },
  "hand-sink": { citation: "OSHA 29 CFR 1910.141(d)(1)", row: "osha-housekeeping#OS17" },
  "trash-cans-indoor": { citation: "OSHA 29 CFR 1910.141(a)(4)(i)", row: "osha-housekeeping#OS12" },
  "trash-cans-outdoor": { citation: "OSHA 29 CFR 1910.141(a)(4)(i)", row: "osha-housekeeping#OS12" },
  "storage-shelves": { citation: "OSHA 29 CFR 1910.176(c)", row: "osha-housekeeping#OS32" },
};

/* ─────────────── the catalog ─────────────── */

/** A requirement (requirements.ts): its citation. Every requirement is a rule; the test proves each resolves to law or OSHA. */
export function basisOfRequirement(id: string): BasisInfo {
  const r = REQUIREMENTS.find((x) => x.id === id);
  return r ? basisOfCitations([r.citation]) : senseInfo();
}

/** Requirement ids (a check, a task, a template, an incident type): the first that is law, else OSHA, else sense. */
export function basisOfRequirements(ids: readonly string[], fallback?: BasisInfo): BasisInfo {
  const infos = ids.map(basisOfRequirement);
  return infos.find((b) => b.basis === "law") ?? infos.find((b) => b.basis === "osha") ?? fallback ?? senseInfo();
}

/** A log template: its requirements. */
export function basisOfTemplate(t: SheetTemplate | string): BasisInfo {
  const tpl = typeof t === "string" ? getTemplate(t) : t;
  return tpl ? basisOfRequirements(tpl.requirementIds, basisOfCitations(tpl.rules.map((r) => r.citation))) : senseInfo();
}

/** One rule on a template ("fridge ≤ 41°F"): its own citation ("Parish protocol: Freezer organization" is sense). */
export const basisOfRule = (r: Pick<RowRule, "citation">): BasisInfo => basisOfCitations([r.citation]);

/** A built-in check (schedule.ts BASE_CHECKS): its requirements. */
export const basisOfCheckRule = (r: Pick<CheckRule, "requirementIds">): BasisInfo => basisOfRequirements(r.requirementIds);

/**
 * A surface's interval (objects.ts intervalFor): the basis of what makes it due. A numeric interval → the strongest
 * citation among the howOften lines stating that interval; a suggested daily one → the maker if a line defers to the
 * maker, else "Suggested" with the reason, and the rule that still asks it be kept clean (a Food Code or OSHA line).
 */
export function basisOfSurface(s: SurfaceGuide | string): BasisInfo {
  const surface = typeof s === "string" ? getSurface(s) : s;
  if (!surface) return senseInfo(SUGGESTED_REASON);
  const iv = intervalFor(surface);
  if (!iv.suggested) {
    const same = surface.howOften.filter((h) => parseIntervalHours(h.when) === iv.hours);
    const infos = same.map((h) => basisOfCitations([h.citation], { text: h.when }));
    return infos.find((b) => b.basis === "law") ?? infos.find((b) => b.basis === "osha") ?? infos.find((b) => b.basis === "maker") ?? senseInfo(iv.reason);
  }
  if (surface.howOften.some((h) => MAKER_RE.test(h.when) || MAKER_RE.test(h.citation))) return makerInfo();
  const keepLaw = pieces(surface.howOften.map((h) => h.citation)).find(isLawCitation);
  const osha = SURFACE_OSHA_RULE[surface.id];
  const rule = keepLaw ? `Keeping it clean: ${keepLaw}` : osha ? `Keeping it clean: ${osha.citation.replace(/^OSHA 29 CFR /, "OSHA ")}` : undefined;
  return senseInfo(SUGGESTED_REASON, rule);
}

/** A task (a starter seed or a location's TaskDefinition, matched to its seed by title): requirements, then OSHA rows, then its guide. */
export function basisOfTask(t: { title: string; requirementIds?: readonly string[]; how?: string; key?: string }): BasisInfo {
  const seed = t.key ? TASK_SEEDS.find((s) => s.key === t.key) : seedForTask(t);
  const reqs = t.requirementIds ?? seed?.requirementIds ?? [];
  const fromReqs = basisOfRequirements(reqs, undefined);
  if (fromReqs.basis === "law" || fromReqs.basis === "osha") return fromReqs;
  const osha = seed ? TASK_OSHA[seed.key] : undefined;
  if (osha) return oshaInfo(osha.citation);
  const how = t.how ?? seed?.how ?? "";
  if (MAKER_RE.test(how)) return makerInfo();
  if (seed?.surfaceId) {
    const s = basisOfSurface(seed.surfaceId);
    if (s.basis === "maker") return s;
  }
  return senseInfo(how ? `${SENSE_REASON} ${how}` : SENSE_REASON);
}

/**
 * The basis of anything the app lists, by what it carries. Order: requirement ids, then citations, then a task title,
 * then a surface. With nothing to go on: sense (B3).
 */
export function basisOf(item: { requirementIds?: readonly string[]; citations?: readonly string[]; citation?: string; title?: string; taskKey?: string; surfaceId?: string; templateId?: string; text?: string }): BasisInfo {
  if (item.requirementIds?.length) {
    const b = basisOfRequirements(item.requirementIds);
    if (b.basis !== "sense") return b;
  }
  const cites = [...(item.citations ?? []), ...(item.citation ? [item.citation] : [])];
  if (cites.length) {
    const b = basisOfCitations(cites, { text: item.text });
    if (b.basis !== "sense") return b;
  }
  if (item.templateId) {
    const b = basisOfTemplate(item.templateId);
    if (b.basis !== "sense") return b;
  }
  if (item.taskKey || item.title) {
    const b = basisOfTask({ title: item.title ?? "", key: item.taskKey, requirementIds: item.requirementIds, how: item.text });
    if (b.basis !== "sense" || !item.surfaceId) return b;
  }
  if (item.surfaceId) return basisOfSurface(item.surfaceId);
  return basisOfCitations([], { text: item.text });
}
